import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  Sparkles,
  Cake,
  Clock,
  Inbox,
  CircleDashed,
  ArrowRight,
} from "lucide-react";
import { api, type AttentionItem, type AttentionItemKind } from "../lib/api";

function isInferred(item: AttentionItem): boolean {
  return item.kind === "recommendation" || item.kind === "follow_up" || item.kind === "brief_item";
}

function kindIcon(kind: AttentionItemKind) {
  switch (kind) {
    case "birthday":
      return Cake;
    case "expiring":
      return Clock;
    case "brief_item":
      return Inbox;
    case "recommendation":
      return Sparkles;
    default:
      return CheckCircle2;
  }
}

/** W076.4 + Day 7: hero strip on /today showing the top-3 attention
 *  items above the fold. Items render with dashed border when inferred
 *  and accept/dismiss directly. */
export function TodayAttentionStrip() {
  const [items, setItems] = useState<AttentionItem[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(() => {
    api
      .attentionList({ surface: "home", limit: 3 })
      .then((r) => setItems((r.items || []).slice(0, 3)))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoaded(true));
  }, []);

  useEffect(refresh, [refresh]);
  useEffect(() => {
    const id = window.setInterval(refresh, 60_000);
    return () => window.clearInterval(id);
  }, [refresh]);

  const onAccept = useCallback(
    async (item: AttentionItem) => {
      setBusyId(item.id);
      try {
        await api.attentionActedOn(item.id);
        setItems((prev) => prev.filter((i) => i.id !== item.id));
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusyId(null);
      }
    },
    [],
  );

  const onDismiss = useCallback(
    async (item: AttentionItem) => {
      setBusyId(item.id);
      try {
        await api.attentionDismiss(item.id);
        setItems((prev) => prev.filter((i) => i.id !== item.id));
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusyId(null);
      }
    },
    [],
  );

  if (!loaded) return null;
  if (items.length === 0) return null;

  return (
    <section aria-label="Attention strip" className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Top of mind
        </h2>
        <Link
          to="/attention"
          className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 dark:text-sky-400 hover:underline"
        >
          See all
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      {error && (
        <div className="rounded-lg border border-rose-300/60 bg-rose-50/60 dark:border-rose-700/40 dark:bg-rose-900/30 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">
          {error}
        </div>
      )}
      <ul className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {items.map((it) => {
          const Icon = kindIcon(it.kind);
          const inferred = isInferred(it);
          return (
            <li key={it.id}>
              <article
                className={
                  "h-full rounded-2xl p-4 space-y-2.5 bg-white/70 dark:bg-slate-900/50 " +
                  (inferred
                    ? "border-2 border-dashed border-amber-400/55 dark:border-amber-500/35"
                    : "border border-slate-200/70 dark:border-slate-700/40")
                }
              >
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-300">
                    <Icon className="w-4 h-4" />
                  </span>
                  {inferred && (
                    <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                      <CircleDashed className="w-3 h-3" />
                      Inferred
                    </span>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {it.title || it.summary || "Untitled"}
                </h3>
                {it.summary && it.title && it.summary !== it.title && (
                  <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">{it.summary}</p>
                )}
                <div className="flex items-center gap-1.5 pt-1">
                  <button
                    type="button"
                    onClick={() => onAccept(it)}
                    disabled={busyId === it.id}
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white px-2.5 py-1.5 text-xs font-semibold disabled:opacity-50 transition"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {inferred ? "Confirm" : "Accept"}
                  </button>
                  <button
                    type="button"
                    onClick={() => onDismiss(it)}
                    disabled={busyId === it.id}
                    className="inline-flex items-center gap-1 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 px-2.5 py-1.5 text-xs font-semibold disabled:opacity-50 transition"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Dismiss
                  </button>
                </div>
              </article>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
